import React from 'react';
import { Tooltip } from '@mui/material';
import { CurrenciesModel } from '@models/profile.model.ts';
import { ProductsModel } from '@models/products.model.ts';
import { formatDate } from '@shared/utils/date-format-utils';
import AppImage from '@components/app-image/app-image.tsx';
import ProductActions from '@modules/products/components/products-table/components/actions/product-actions.tsx';
import {
  IsPublishedStatusMap,
  isPublishedStatusOptions
} from '@utils/status-options.utils.ts';

export const productsColumns = (
  handlePublish: (row: ProductsModel) => void,
  handleEdit: (row: ProductsModel) => void,
  handleDelete: (row: ProductsModel) => void,
  selectedCurrency: CurrenciesModel | null,
  language: string,
  t: (key: string) => string
) => [
  {
    id: 'image',
    label: 'column_label_image',
    field: 'image',
    render: (value: any) => (
      <div
        className={`h-13 w-13 rounded-md border border-(--gray-7) overflow-hidden cursor-pointer`}
      >
        <AppImage imgClassName="!h-full" filePath={value ?? undefined} />
      </div>
    ),
    showSort: false,
    showSearch: false,
    showFilter: false
  },
  {
    id: 'name',
    label: 'column_label_name',
    field: 'name',
    render: (value: any) => (
      <Tooltip title={value?.[language] || 'N/A'} followCursor>
        <span>{value?.[language] || 'N/A'} </span>
      </Tooltip>
    ),
    showSort: false,
    showSearch: true,
    showFilter: false,
    isTranslated: true
  },
  {
    id: 'category.name',
    label: 'column_label_category',
    field: 'category',
    render: (value: any) => (
      <Tooltip title={value?.name?.[language] || 'N/A'} followCursor>
        <span>{value?.name?.[language] || 'N/A'} </span>
      </Tooltip>
    ),
    showSort: false,
    showSearch: true,
    showFilter: false,
    isTranslated: true
  },
  {
    id: 'description',
    label: 'column_label_description',
    field: 'description',
    render: (value: any) => (
      <Tooltip title={value?.[language] || 'N/A'} followCursor>
        <span>{value?.[language] || 'N/A'} </span>
      </Tooltip>
    ),
    showSort: false,
    showSearch: true,
    showFilter: false
  },
  {
    id: 'unitPrice',
    label: 'column_label_price',
    field: 'unitPrice',
    render: (value: any) => (
      <Tooltip title={`${value} ${selectedCurrency?.symbol}`} followCursor>
        <span>
          {value ? `${value} ${selectedCurrency?.symbol || ''}` : '-'}
        </span>
      </Tooltip>
    ),
    showSort: true,
    showSearch: true,
    showFilter: false
  },
  {
    id: 'deliveryTime',
    label: 'column_label_delivery_time',
    field: 'deliveryTime',
    render: (value: any) => (
      <Tooltip title={value || 'N/A'} followCursor>
        <span>{value || 'N/A'} </span>
      </Tooltip>
    ),
    showSort: false,
    showSearch: false,
    showFilter: false
  },
  {
    id: 'isPublished',
    label: 'column_label_status',
    field: 'isPublished',
    render: (value: boolean) => {
      const status = IsPublishedStatusMap[String(value) as 'true' | 'false'];

      return (
        <Tooltip
          title={t(status?.text) || (value ? 'Published' : 'Unpublished')}
          followCursor
        >
          <div
            style={{
              background: status?.background || 'inherit',
              color: status?.color || 'inherit',
              fontWeight: 600,
              textAlign: 'center',
              padding: '6px 12px',
              borderRadius: '6px'
            }}
          >
            {t(status?.text) || (value ? 'Published' : 'Unpublished')}
          </div>
        </Tooltip>
      );
    },
    showSort: false,
    showSearch: false,
    showFilter: true,
    filterData: isPublishedStatusOptions,
    showSearchInput: false
  },
  {
    id: 'createdAt',
    label: 'column_label_createdAt',
    field: 'createdAt',
    render: (value: any) => (
      <Tooltip title={value ? formatDate(value) : '-'} followCursor>
        <span>{value ? formatDate(value) : '-'}</span>
      </Tooltip>
    ),
    showSort: true,
    showSearch: true,
    showFilter: false
  },
  {
    id: 'Action',
    label: 'column_label_actions',
    field: 'actions',
    width: 110,
    render: (value: any, row: any) => (
      <ProductActions
        row={row}
        handlePublish={handlePublish}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
      />
    ),
    showSort: false,
    showSearch: false,
    showFilter: false
  }
];
